<template>
  <v-app>
    <v-container fluid>
      <v-card class="mt-4">
        <v-card-title>
          Users with Inactive Products
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Search by name, email or phone"
            single-line
            hide-details
            style="max-width: 300px;"
          ></v-text-field>
        </v-card-title>

        <v-card-text>
          <div class="mb-3">
            <v-btn small color="green darken-1" dark class="mr-2" @click="exportCSV">Export CSV</v-btn>
            <v-btn small color="red darken-1" dark class="mr-2" @click="exportPDF">Export PDF</v-btn>
            <v-btn small color="blue darken-1" dark @click="exportDOCX">Export DOCX</v-btn>
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            :loading="!usersInactiveProducts"
            item-key="user_id"
            expand
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr @click="props.expanded = !props.expanded" style="cursor:pointer;">
                <td>{{ props.item.fullname }}</td>
                <td>{{ props.item.email }}</td>
                <td>{{ props.item.phone }}</td>
                <td>{{ formatCurrency(props.item.total_amount) }}</td>
                <td>{{ formatCurrency(props.item.total_interest) }}</td>
                <td style="color:#c62828;">{{ formatCurrency(props.item.total_withdrawn) }}</td>
                <td style="color:#e65100; font-weight:bold;">{{ formatCurrency((props.item.total_amount + props.item.total_interest) - props.item.total_withdrawn) }}</td>
                <td><v-icon>{{ props.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon></td>
              </tr>
            </template>
            <template v-slot:expand="props">
              <v-card flat style="background:#fff3e0; padding:12px;">
                <table style="width:100%; border-collapse:collapse;">
                  <thead>
                    <tr style="background:#ffe0b2;">
                      <th style="padding:8px; text-align:left;">Product</th>
                      <th style="padding:8px; text-align:right;">Invested</th>
                      <th style="padding:8px; text-align:right;">Interest</th>
                      <th style="padding:8px; text-align:right;">Withdrawn</th>
                      <th style="padding:8px; text-align:right;">Balance</th>
                      <th style="padding:8px; text-align:left;">Start Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, i) in props.item.products" :key="i" style="border-bottom:1px solid #ddd;">
                      <td style="padding:8px;">{{ p.product_name }}</td>
                      <td style="padding:8px; text-align:right;">{{ formatCurrency(p.amount) }}</td>
                      <td style="padding:8px; text-align:right;">{{ formatCurrency(p.interest) }}</td>
                      <td style="padding:8px; text-align:right; color:#c62828;">{{ formatCurrency(p.withdrawn) }}</td>
                      <td style="padding:8px; text-align:right; color:#e65100; font-weight:bold;">{{ formatCurrency((p.amount + p.interest) - p.withdrawn) }}</td>
                      <td style="padding:8px;">{{ formatDate(p.start_date) }}</td>
                    </tr>
                  </tbody>
                </table>
              </v-card>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </v-app>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'UsersInactiveProducts',
  data() {
    return {
      search: '',
      headers: [
        { text: 'Name', value: 'fullname' },
        { text: 'Email', value: 'email' },
        { text: 'Phone', value: 'phone' },
        { text: 'Total Invested', value: 'total_amount' },
        { text: 'Total Interest', value: 'total_interest' },
        { text: 'Total Withdrawn', value: 'total_withdrawn' },
        { text: 'Balance Remaining', value: 'balance_remaining' },
        { text: '', value: '', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(['usersInactiveProducts']),
    usersWithFullname() {
      return (this.usersInactiveProducts || []).map(u => ({
        ...u,
        fullname: `${u.firstname} ${u.lastname}`,
      }));
    },
    filteredUsers() {
      if (!this.search) return this.usersWithFullname;
      const q = this.search.toLowerCase();
      return this.usersWithFullname.filter(u =>
        u.fullname.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.phone && u.phone.includes(q))
      );
    },
  },
  created() {
    this.getUsersInactiveProducts();
  },
  methods: {
    ...mapActions(['getUsersInactiveProducts']),

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
    },

    formatDate(val) {
      if (!val) return '-';
      return new Date(val).toLocaleDateString('en-NG');
    },

    download(filename, content, mime) {
      const blob = new Blob([content], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    },

    exportCSV() {
      const rows = [];
      rows.push(['Name', 'Email', 'Phone', 'Product', 'Invested (NGN)', 'Interest (NGN)', 'Withdrawn (NGN)', 'Balance (NGN)', 'Start Date'].join(','));
      this.filteredUsers.forEach(u => {
        u.products.forEach(p => {
          rows.push([
            `"${u.fullname}"`, `"${u.email}"`, `"${u.phone || ''}"`,
            `"${p.product_name}"`,
            p.amount.toFixed(2), p.interest.toFixed(2),
            p.withdrawn.toFixed(2),
            ((p.amount + p.interest) - p.withdrawn).toFixed(2),
            this.formatDate(p.start_date),
          ].join(','));
        });
        rows.push([
          `"${u.fullname}"`, `"${u.email}"`, `"${u.phone || ''}"`, '"TOTAL"',
          u.total_amount.toFixed(2), u.total_interest.toFixed(2),
          u.total_withdrawn.toFixed(2),
          ((u.total_amount + u.total_interest) - u.total_withdrawn).toFixed(2), '',
        ].join(','));
        rows.push('');
      });
      this.download('users-inactive-products.csv', rows.join('\n'), 'text/csv;charset=utf-8;');
    },

    exportPDF() {
      const win = window.open('', '_blank');
      const rows = this.filteredUsers.map(u => `
        <tr style="background:#fff3e0; font-weight:bold;">
          <td colspan="6">${u.fullname} &mdash; ${u.email} &mdash; ${u.phone || ''}</td>
        </tr>
        ${u.products.map(p => `
          <tr>
            <td>${p.product_name}</td>
            <td style="text-align:right;">${p.amount.toFixed(2)}</td>
            <td style="text-align:right;">${p.interest.toFixed(2)}</td>
            <td style="text-align:right; color:#c62828;">${p.withdrawn.toFixed(2)}</td>
            <td style="text-align:right; font-weight:bold;">${((p.amount + p.interest) - p.withdrawn).toFixed(2)}</td>
            <td>${this.formatDate(p.start_date)}</td>
          </tr>
        `).join('')}
        <tr style="background:#ffe0b2; font-weight:bold;">
          <td>TOTAL</td>
          <td style="text-align:right;">${u.total_amount.toFixed(2)}</td>
          <td style="text-align:right;">${u.total_interest.toFixed(2)}</td>
          <td style="text-align:right; color:#c62828;">${u.total_withdrawn.toFixed(2)}</td>
          <td style="text-align:right;">${((u.total_amount + u.total_interest) - u.total_withdrawn).toFixed(2)}</td>
          <td></td>
        </tr>
        <tr><td colspan="6" style="padding:4px;"></td></tr>
      `).join('');

      win.document.write(`
        <html><head><title>Users Inactive Products</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px; }
          th { background: #e65100; color: #fff; }
          @media print { button { display: none; } }
        </style></head>
        <body>
          <h2>Users with Inactive Products</h2>
          <p>Generated: ${new Date().toLocaleString('en-NG')}</p>
          <button onclick="window.print()" style="margin-bottom:12px; padding:6px 16px;">Print / Save as PDF</button>
          <table>
            <thead>
              <tr><th>Product</th><th>Invested (NGN)</th><th>Interest (NGN)</th><th>Withdrawn (NGN)</th><th>Balance (NGN)</th><th>Start Date</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body></html>
      `);
      win.document.close();
    },

    exportDOCX() {
      const rows = this.filteredUsers.map(u => `
        <w:tr>
          <w:tc><w:tcPr><w:gridSpan w:val="6"/></w:tcPr>
            <w:p><w:r><w:rPr><w:b/></w:rPr><w:t>${u.fullname} — ${u.email} — ${u.phone || ''}</w:t></w:r></w:p>
          </w:tc>
        </w:tr>
        ${u.products.map(p => `
          <w:tr>
            <w:tc><w:p><w:r><w:t>${p.product_name}</w:t></w:r></w:p></w:tc>
            <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:t>${p.amount.toFixed(2)}</w:t></w:r></w:p></w:tc>
            <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:t>${p.interest.toFixed(2)}</w:t></w:r></w:p></w:tc>
            <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:t>${p.withdrawn.toFixed(2)}</w:t></w:r></w:p></w:tc>
            <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:t>${((p.amount + p.interest) - p.withdrawn).toFixed(2)}</w:t></w:r></w:p></w:tc>
            <w:tc><w:p><w:r><w:t>${this.formatDate(p.start_date)}</w:t></w:r></w:p></w:tc>
          </w:tr>
        `).join('')}
        <w:tr>
          <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>TOTAL</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:rPr><w:b/></w:rPr><w:t>${u.total_amount.toFixed(2)}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:rPr><w:b/></w:rPr><w:t>${u.total_interest.toFixed(2)}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:rPr><w:b/></w:rPr><w:t>${u.total_withdrawn.toFixed(2)}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:pPr><w:jc w:val="right"/></w:pPr><w:r><w:rPr><w:b/></w:rPr><w:t>${((u.total_amount + u.total_interest) - u.total_withdrawn).toFixed(2)}</w:t></w:r></w:p></w:tc>
          <w:tc><w:p><w:r><w:t></w:t></w:r></w:p></w:tc>
        </w:tr>
      `).join('');

      const xml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<?mso-application progid="Word.Document"?>
<w:wordDocument xmlns:w="http://schemas.microsoft.com/office/word/2003/wordml">
  <w:body>
    <w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Users with Inactive Products</w:t></w:r></w:p>
    <w:p><w:r><w:t>Generated: ${new Date().toLocaleString('en-NG')}</w:t></w:r></w:p>
    <w:tbl>
      <w:tblPr><w:tblW w:w="0" w:type="auto"/><w:tblBorders>
        <w:top w:val="single" w:sz="4"/><w:left w:val="single" w:sz="4"/>
        <w:bottom w:val="single" w:sz="4"/><w:right w:val="single" w:sz="4"/>
        <w:insideH w:val="single" w:sz="4"/><w:insideV w:val="single" w:sz="4"/>
      </w:tblBorders></w:tblPr>
      <w:tr>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Product</w:t></w:r></w:p></w:tc>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Invested (NGN)</w:t></w:r></w:p></w:tc>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Interest (NGN)</w:t></w:r></w:p></w:tc>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Withdrawn (NGN)</w:t></w:r></w:p></w:tc>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Balance (NGN)</w:t></w:r></w:p></w:tc>
        <w:tc><w:p><w:r><w:rPr><w:b/></w:rPr><w:t>Start Date</w:t></w:r></w:p></w:tc>
      </w:tr>
      ${rows}
    </w:tbl>
  </w:body>
</w:wordDocument>`;

      this.download('users-inactive-products.doc', xml, 'application/msword');
    },
  },
};
</script>
